import { useState, useEffect, useRef, useCallback } from "react";
import { fetchOrderTrackingDetails } from "../../store/thunk/adminThunk";
import { useNavigate, useSearchParams } from "react-router-dom";
import { decodeFromBase64 } from "../../hooks/commonFunction";
import { useDispatch } from "react-redux";
import toast from "react-hot-toast";
import PageLoader from "../../shared/loader/PageLoader";
import { MapPin, Navigation, Phone, Store, Truck } from "lucide-react";
import storeMarker from "../../assets/img/icons/store-marker.png";
import defaultOrderMarker from "../../assets/img/icons/default-order-marker.png";
import deliveryManMarker from "../../assets/img/icons/delivery-man-marker.png";
const OrderTracking = () => {
  const [searchParams] = useSearchParams();
  const encodedTrackingId = searchParams.get("tracking-id");
  const trackingId = encodedTrackingId
    ? decodeFromBase64(encodedTrackingId)
    : null;
  const [map, setMap] = useState(null);
  const [deliveryMarker, setDeliveryMarker] = useState(null);
  const [directionsService, setDirectionsService] = useState(null);
  const [directionsRenderer, setDirectionsRenderer] = useState(null);
  const [distance, setDistance] = useState("");
  const [estimatedTime, setEstimatedTime] = useState("");
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Initialize with default values
  const [driverLocation, setDriverLocation] = useState({
    lat: 34.0522,
    lng: -118.2437,
  });
  const [homeLocation, setHomeLocation] = useState({});
  const [driverInfo, setDriverInfo] = useState({});

  const [deliveryType, setDeliveryType] = useState(null);
  const [deliveryData, setDeliveryData] = useState(null);
  const [trackingData, setTrackingData] = useState(null);
  const [loading, setLoading] = useState(false);
  const mapRef = useRef(null);
  const intervalRef = useRef(null);
  const homeMarkerRef = useRef(null);

  const [isUserInteracting, setIsUserInteracting] = useState(false);
  const isUserInteractingRef = useRef(isUserInteracting); 
  const isInitializedRef = useRef(false);

  useEffect(() => {
      isUserInteractingRef.current = isUserInteracting;
  }, [isUserInteracting]);

  // Load Google Maps Script
  useEffect(() => {
    if (window.google) {
      setIsMapLoaded(true);
      return;
    }

    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&libraries=geometry`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      setIsMapLoaded(true);
    };
    script.onerror = () => {
      console.error("Failed to load Google Maps");
    };
    document.head.appendChild(script);
  }, []);

  // Fetch tracking data
  useEffect(() => {
    if (!trackingId) {
      navigate("/order-tracking-list");
      return;
    }
    // Initial fetch
    fetchTrackingInfo();

    // Set up polling interval and store in ref
    intervalRef.current = setInterval(() => {
        fetchTrackingInfo();
    }, 20000);

    // Cleanup function
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [trackingId, navigate]);

  const fetchTrackingInfo = async () => {
    //setLoading(true);
    try {
      const result = await dispatch(
        fetchOrderTrackingDetails({ tracking_id: trackingId })
      ).unwrap();

      // Check if order is delivered (status = 1)
      if (result?.data?.order_tracking_info?.status === 1) {
        // Clear interval when delivered
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      }

      if (result.status === 0) {
        toast.error(result.message || "Failed to fetch tracking details");
        setTrackingData(null);
        navigate("/order-tracking-list");
        return;
      }

      // Success - store the data
      setTrackingData(result.data || result);
      if(!isUserInteractingRef.current){
        const driverLocationData = result?.data?.order_tracking_info;
        // Update driver location from selectedData
        if (driverLocationData?.latitude && driverLocationData?.longitude) {
          const newDriverLocation = {
            lat: parseFloat(driverLocationData.latitude),
            lng: parseFloat(driverLocationData.longitude),
          };
          setDriverLocation(newDriverLocation);

          // Update delivery marker position if it exists
          if (deliveryMarker) {
            deliveryMarker.setPosition(newDriverLocation);
            deliveryMarker.setVisible(true);
          } else {
            console.warn("Delivery marker not yet created");
          }
        } else {
          console.warn("No driver location data available:", driverLocationData);
        }

        // Check driver_locations to determine delivery type
        if (result?.data?.driver_locations) {
          const driverLocations = result?.data?.driver_locations;

          let selectedType = null;
          let selectedData = null;

          // Check if default has data
          if (driverLocations?.default && driverLocations?.default?.length > 0) {
            selectedType = "default";
            selectedData = driverLocations.default[0]; 
          }
          // Check if delivery_partner has data
          else if (
            driverLocations?.delivery_partner &&
            driverLocations?.delivery_partner?.length > 0
          ) {
            selectedType = "delivery_partner";
            selectedData = driverLocations.delivery_partner[0]; // Get first item
            //console.log("Delivery Type: delivery_partner", selectedData);
          }

          if (selectedType && selectedData) {
            setDeliveryType(selectedType);
            setDeliveryData(selectedData);

            // Update home/delivery location from selectedData
            if (selectedData?.latitude && selectedData?.longitude) {
              const newHomeLocation = {
                latitude: parseFloat(selectedData.latitude),
                longitude: parseFloat(selectedData.longitude),
                location: selectedData.location || "",
                location_name: selectedData.location_name || "",
                tracking_id: selectedData.tracking_id || "",
              };

              setHomeLocation(newHomeLocation);

              // Update home marker position if it exists
              if (homeMarkerRef.current) {
                const homePos = {
                  lat: parseFloat(newHomeLocation.latitude),
                  lng: parseFloat(newHomeLocation.longitude),
                };
                homeMarkerRef.current.setPosition(homePos);
              }

              //console.log("Home location updated:", newHomeLocation);
            }

            // Update driver info
            if (selectedData.driver_name) {
              setDriverInfo({
                name: selectedData.driver_name,
                phone: result?.data?.driver_phone_number,
              });
            }

            // Recenter map and redraw route
            if (map && selectedData.latitude && selectedData.longitude && !isUserInteracting) {
              const driverPos = {
                lat: parseFloat(selectedData.latitude),
                lng: parseFloat(selectedData.longitude),
              };

              const homePos = {
                lat: parseFloat(selectedData.latitude),
                lng: parseFloat(selectedData.longitude),
              };

              // Recenter map
              const centerLat = (driverPos.lat + homePos.lat) / 2;
              const centerLng = (driverPos.lng + homePos.lng) / 2;
              map.setCenter({ lat: centerLat, lng: centerLng });
              // Redraw route
              drawRoute(driverPos, homePos);
            }
            isInitializedRef.current = false;
          } else {
            console.warn("No driver location data found");
            toast.warning("No active delivery found for this tracking ID");
          }
        }
      }  
    } catch (error) {
      const errorMessage = error?.message || "Failed to fetch tracking details";
      toast.error(errorMessage);
      console.error("Tracking fetch error:", error);
      isInitializedRef.current = false;
    } finally {
      //setLoading(false);
    }
  };

  // Draw route between two points
  const drawRoute = useCallback(
    (from, to) => {
      if (!directionsService || !directionsRenderer) return;

      const request = {
        origin: new window.google.maps.LatLng(from.lat, from.lng),
        destination: new window.google.maps.LatLng(to.lat, to.lng),
        travelMode: window.google.maps.TravelMode.DRIVING,
      };

      directionsService.route(request, (result, status) => {
        if (status === window.google.maps.DirectionsStatus.OK) {
          directionsRenderer.setDirections(result);

          const route = result.routes[0];
          const distanceValue = route.legs.reduce(
            (sum, leg) => sum + leg.distance.value,
            0
          );
          const durationValue = route.legs.reduce(
            (sum, leg) => sum + leg.duration.value,
            0
          );

          // Convert to miles
          const distanceInMiles = (distanceValue * 0.000621371).toFixed(1);
          setDistance(distanceInMiles);

          // Calculate ETA in minutes
          const etaMinutes = Math.round(durationValue / 60);
          setEstimatedTime(etaMinutes);
        }
      });
    },
    [directionsService, directionsRenderer]
  );

  // Initialize Google Maps
  useEffect(() => {
    if (!isMapLoaded || !window.google || !mapRef.current || map) return;

    // Calculate center point
    const homePos = {
      lat: parseFloat(homeLocation.latitude),
      lng: parseFloat(homeLocation.longitude),
    };

    const centerLat = (driverLocation.lat + homePos.lat) / 2;
    const centerLng = (driverLocation.lng + homePos.lng) / 2;

    // Create map
    const mapInstance = new window.google.maps.Map(mapRef.current, {
      zoom: 13,
      center: { lat: centerLat, lng: centerLng },
      disableDefaultUI: false,
      zoomControl: true,
      mapTypeControl: true,
      streetViewControl: false,
      fullscreenControl: true,
    });
    setMap(mapInstance);
    // Add event listeners for user interaction
    const setUserInteractingTrue = () => {
        // Only set true if the map has finished its initial data-driven centering/route
        if (isInitializedRef.current) {
            setIsUserInteracting(true);
        }else{
          isInitializedRef.current = true;
        }
    };

    mapInstance.addListener('dragstart', setUserInteractingTrue);
    mapInstance.addListener('zoom_changed', setUserInteractingTrue);

    // Determine home marker icon based on delivery type
    const homeMarkerIcon = deliveryType === "delivery_partner" ? storeMarker : defaultOrderMarker;

    // Add home location marker
    const homeMarker = new window.google.maps.Marker({
      position: homePos,
      map: mapInstance,
      title: deliveryType === "delivery_partner" ? "Store Location" : "Delivery Location",
      icon: {
        url: homeMarkerIcon,
        scaledSize: new window.google.maps.Size(50, 50),
        anchor: new window.google.maps.Point(25, 25), // Center of the icon
      },
    });
    homeMarkerRef.current = homeMarker;

    // Fallback icon in case of error
    const fallbackIcon = {
      path: window.google.maps.SymbolPath.CIRCLE,
      scale: 12,
      fillColor: "#4CAF50",
      fillOpacity: 1,
      strokeColor: "#ffffff",
      strokeWeight: 3,
    };

    let deliveryIcon;
    try {
      deliveryIcon = {
        url: deliveryManMarker,
        scaledSize: new window.google.maps.Size(50, 50),
        anchor: new window.google.maps.Point(25, 25),
      };
    } catch (error) {
      console.error("Error loading delivery marker icon:", error);
      deliveryIcon = fallbackIcon;
    }

    const deliveryMarkerInstance = new window.google.maps.Marker({
      map: mapInstance,
      position: driverLocation,
      icon: deliveryIcon,
      title: "Delivery Agent",
      optimized: false,
      visible: true, // Explicitly set visible
    });
    setDeliveryMarker(deliveryMarkerInstance);

    // Initialize directions
    const dirService = new window.google.maps.DirectionsService();
    const dirRenderer = new window.google.maps.DirectionsRenderer({
      suppressMarkers: true,
      polylineOptions: {
        strokeColor: "#C62828",
        strokeWeight: 5,
        strokeOpacity: 0.8,
      },
    });
    dirRenderer.setMap(mapInstance);
    setDirectionsService(dirService);
    setDirectionsRenderer(dirRenderer);
  }, [isMapLoaded, map, driverLocation, homeLocation]);

  // Update home marker icon when delivery type changes
  useEffect(() => {
    if (homeMarkerRef.current && deliveryType !== null) {
      const homeMarkerIcon = deliveryType === "delivery_partner" ? storeMarker : defaultOrderMarker;
      homeMarkerRef.current.setIcon({
        url: homeMarkerIcon,
        scaledSize: new window.google.maps.Size(50, 50),
        anchor: new window.google.maps.Point(25, 25),
      });
      homeMarkerRef.current.setTitle(
        deliveryType === "delivery_partner" ? "Store Location" : "Delivery Location"
      );
    }
  }, [deliveryType]);

  // Update delivery marker position when driver location changes
  useEffect(() => {
    if (deliveryMarker && driverLocation) {
      deliveryMarker.setPosition(driverLocation);
      deliveryMarker.setVisible(true);
    }
  }, [deliveryMarker, driverLocation]);

  // Draw initial route when map is ready
  useEffect(() => {
    if (!map || !directionsService) return;

    const destination = {
      lat: parseFloat(homeLocation.latitude),
      lng: parseFloat(homeLocation.longitude),
    };

    const timeoutId = setTimeout(() => {
      drawRoute(driverLocation, destination);
    }, 1000);

    return () => clearTimeout(timeoutId);
  }, [map, directionsService, drawRoute, driverLocation, homeLocation]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  const handleRecenter = useCallback(() => {
    isInitializedRef.current = false;
    setIsUserInteracting(false);
    fetchTrackingInfo();
  }, []);

  if (loading) {
    return <PageLoader />;
  }
  return (
    <section className="overflow-y-auto relative no-scrollbar">
      <div className="container px-0">
        <div className="p-4">
          {/* Header - Hidden on mobile */}
          <div className="hidden md:block bg-white border-b border-gray-200 py-4 mb-4">
            <h1 className="text-3xl font-bold text-red-600">Orders Tracking</h1>
          </div>

          {/* Mobile: Vertical Layout, Desktop: Horizontal Layout */}
          <div className="flex flex-col md:flex-row md:h-[calc(100vh-150px)]">
            {/* Map Section */}
            <div className="relative w-full md:flex-1 h-[50vh] md:h-full">
              {/* Conditional Rendering: Success Message or Tracking Info */}
              {trackingData?.order_tracking_info?.status === 1 ? (
                // SUCCESS SECTION - Order Delivered
                <div className="absolute inset-0 flex items-center justify-center z-20">
                  <div className="text-center md:px-6 py-8 max-w-xl">
                    <div className="mb-1 md:mb-6">
                      <div className="mx-auto w-20 h-20 bg-green-500 rounded-full flex items-center justify-center">
                        <Truck className="w-12 h-12 text-white" />
                      </div>
                    </div>
                    <h2 className="text-3xl md:text-4xl font-bold text-green-600 mb-4">
                      Order Delivered!
                    </h2>
                    <p className="text-lg md:text-xl text-gray-700 mb-2">
                      Your order has been successfully delivered.
                    </p>
                    <p className="text-md text-gray-600 mb-6">
                      You have successfully completed all your scheduled
                      deliveries from{" "}
                      {trackingData?.order_tracking_info?.start_time
                        ? new Date(
                            trackingData.order_tracking_info.start_time
                          ).toLocaleString("en-US", {
                            month: "2-digit",
                            day: "2-digit",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: true,
                          })
                        : "N/A"}{" "}
                      to{" "}
                      {trackingData?.order_tracking_info?.end_time
                        ? new Date(
                            trackingData.order_tracking_info.end_time
                          ).toLocaleString("en-US", {
                            month: "2-digit",
                            day: "2-digit",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: true,
                          })
                        : "N/A"}
                      .
                    </p>
                    <button
                      onClick={() => navigate("/orders")}
                      className="px-6 py-3 rounded-full text-white text-md bg-hf-gradient"
                    >
                      Back to Orders
                    </button>
                  </div>
                </div>
              ) : (
                // TRACKING SECTION - Order in Transit
                <>
                  {/* Driver Status Banner */}
                  <div className="absolute top-0 left-0 right-0 z-10 bg-gradient-to-r from-red-600 to-red-500 text-white py-3 md:py-4 px-4 md:px-6 shadow-lg">
                    <div className="text-center">
                      <h2 className="text-lg md:text-2xl font-semibold mb-1 md:mb-2">
                        Your Driver is on the way!
                      </h2>
                      <p className="text-sm md:text-lg">
                        ETA:{" "}
                        {estimatedTime ? `${estimatedTime} mins` : "– mins"}{" "}
                        Distance: {distance ? `${distance} miles` : "– miles"}
                      </p>
                    </div>
                  </div>

                  {/* Map Controls - Hidden on mobile */}
                  <div className="hidden md:block absolute top-32 left-4 z-10 bg-white rounded-lg shadow-md overflow-hidden">
                    <button className="px-4 py-2 bg-white text-gray-700 font-medium border-b border-gray-200 hover:bg-gray-50">
                      Map
                    </button>
                    <button className="px-4 py-2 bg-white text-gray-700 font-medium hover:bg-gray-50">
                      Satellite
                    </button>
                  </div>

                  {/* Loading Indicator */}
                  {!isMapLoaded && (
                    <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
                      <div className="text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto mb-4"></div>
                        <p className="text-gray-600">Loading map...</p>
                      </div>
                    </div>
                  )}

                  {/* Map Container */}
                  <div ref={mapRef} className="w-full h-full" />
                  {isUserInteracting && ( 
                      <button
                        onClick={handleRecenter}
                        className="absolute bottom-10 md:bottom-10 w-100 h-10 left-1 z-10 bg-white hover:bg-gray-50 text-gray-700 font-medium px-4 py-3 rounded-lg shadow-lg border border-gray-200 transition-all duration-300 ease-in-out transform hover:scale-105 flex items-center space-x-2 group"
                        aria-label="Recenter map to tracked position"
                        title="Return to tracked position"
                      >
                        <Navigation className="w-5 h-5 text-red-600 group-hover:text-red-700 transition-colors" />
                        <span className="text-sm md:text-base">Recenter</span>
                      </button>
                    )}
                </>
              )}
            </div>

            {/* Driver Info - Sidebar on desktop, Bottom section on mobile */}
            <div className="w-full md:w-80 bg-white md:border-l border-t md:border-t-0 border-gray-200 p-4 md:p-6 overflow-y-auto">
              <h2 className="text-xl md:text-2xl font-bold text-red-600 mb-4 md:mb-6">
                Driver Information
              </h2>

              {/* Driver Details */}
              <div className="space-y-3 md:space-y-4 mb-6 md:mb-8">
                <div className="flex items-start space-x-3">
                  <Truck className="w-5 h-5 md:w-6 md:h-6 text-red-600 flex-shrink-0 mt-1" />
                  <span className="text-base md:text-lg">
                    {driverInfo?.name}
                  </span>
                </div>

                <div className="flex items-center space-x-3">
                  <Phone className="w-5 h-5 md:w-6 md:h-6 text-red-600 flex-shrink-0 mt-1" />
                  <a
                    href={`tel:${driverInfo.phone}`}
                    className="text-base md:text-lg text-gray-700 hover:text-red-600 transition-colors"
                  >
                    {driverInfo.phone}
                  </a>
                </div>
              </div>

              {/* Home Delivery Info */}
              <div>
                <h3 className="text-xl md:text-2xl font-bold text-red-600 mb-3 md:mb-4">
                  {deliveryType === "delivery_partner"
                    ? "Delivery Partner"
                    : "Home Delivery"}
                </h3>
                <div className="space-y-3">
                  {deliveryType === "delivery_partner" && (
                    <div className="flex items-start space-x-3">
                      <Store className="w-5 h-5 md:w-6 md:h-6 text-red-600 flex-shrink-0 mt-1" />
                      <span className="text-base md:text-lg">
                        {homeLocation.location_name}
                      </span>
                    </div>
                  )}
                  {homeLocation?.location && (
                    <div className="flex items-start space-x-3">
                      <MapPin className="w-5 h-5 md:w-6 md:h-6 text-red-600 flex-shrink-0 mt-1" />
                      <span className="text-base md:text-lg">
                        {homeLocation.location}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default OrderTracking;
